import { useFieldRecompute } from '@/modules/Project/useFieldRecompute'
import type { HubFileFields } from '@/modules/Workspaces/KnowledgeHub/Hubs/getHubFileFields'
import type { DotIndicatorColor } from '@/uiKit/DotIndicator.vue'
import { computed, toValue, type ComputedRef, type Ref } from 'vue'

export type HubEntityProcessingStatus = 'error' | 'processing' | 'idle'

export function useHubEntityStatus(fieldsRef: Ref<HubFileFields>, projectId: Ref<string>) {
  const { recomputeField } = useFieldRecompute()

  const fieldList = computed(() => {
    return Object.values(toValue(fieldsRef))
  })

  const firstFieldWithError = computed(() => {
    return fieldList.value.find((field) => field.status === 'error')
  })

  const firstComputingField = computed(() => {
    return fieldList.value.find((field) => field.status === 'computing')
  })

  const isInvalidDocumentType = computed(() => {
    const value = fieldsRef.value.documentType.toolValue || []
    return value.includes('invalid')
  })

  const status: ComputedRef<HubEntityProcessingStatus> = computed(() => {
    if (firstFieldWithError.value || isInvalidDocumentType.value) {
      return 'error'
    }

    if (firstComputingField.value) {
      return 'processing'
    }

    return 'idle'
  })

  const dotIndicatorReason: ComputedRef<{
    color: DotIndicatorColor
    tooltip?: {
      title: string
      body: string | undefined
    }
  } | null> = computed(() => {
    if (firstFieldWithError.value) {
      return {
        color: 'red',
        tooltip: {
          title: 'An error occurred',
          body: firstFieldWithError.value.errorMessage || undefined,
        },
      }
    }

    if (isInvalidDocumentType.value) {
      return {
        color: 'red',
        tooltip: { title: 'An error occurred', body: 'Unsupported document type' },
      }
    }

    if (firstComputingField.value) {
      return { color: 'purple', tooltip: undefined }
    }

    if (status.value === 'idle') {
      return { color: 'green', tooltip: undefined }
    }

    return null
  })

  const hasFieldErrors = computed(() => {
    return !!firstFieldWithError.value
  })

  async function recompute() {
    const failedFields = fieldList.value.filter((field) => field.status === 'error')
    return Promise.all(
      failedFields.map((field) => recomputeField(field, { projectId: toValue(projectId) })),
    )
  }

  return {
    dotIndicatorReason,
    hasFieldErrors,
    recompute,
    status,
  }
}
